/**
 * "Leer dit" (sessie 84): de lessen in de draad van één copiloot, gedeeld
 * door het copilootvlak in de zaak (v6) en de copiloot op de andere
 * pagina's.
 *
 * - Bij openen haalt hij de open en recent vastgezette lessen van deze
 *   pagina of zaak op. Zo is een open voorstel na herladen of na een
 *   zaakwissel en terug gewoon weer in beeld.
 * - Wat JP net liet maken staat eronder, als beurt met de vraag erbij.
 *   Zit een net gemaakte les ook in de lijst van de server, dan staat hij
 *   er één keer.
 * - Elke beurt hoort bij de context waarin hij gemaakt is. Bij een wissel
 *   verdwijnen de beurten van de vorige context meteen (geen flits), en
 *   een antwoord dat pas na de wissel binnenkomt belandt nooit bij een
 *   andere klant of zaak.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  type Les,
  type LesContext,
  leerDitApi,
  lesContext,
  lesFout,
  useLessenVoorContext,
} from "./leerDit";

/** Eén "leer dit"-beurt: de vraag van JP en daaronder de lesvoorstelkaart. */
export interface LesBeurt {
  sleutel: string;
  vraag: string;
  les: Les | null;
  fout: string | null;
}

interface LokaleBeurt extends LesBeurt {
  ctx: string;
}

function ctxSleutel(context: LesContext | null): string {
  return context ? `${context.soort}:${context.id ?? ""}` : "";
}

export function useLesDraad(context: LesContext | null) {
  const soort = context?.soort ?? null;
  const id = context ? lesContext(context.soort, context.id).id : null;
  const ctx = useMemo(
    () => (soort === null ? null : { soort, id }),
    [soort, id],
  );
  const sleutel = ctxSleutel(ctx);
  const lijst = useLessenVoorContext(ctx);

  const [lokaal, setLokaal] = useState<LokaleBeurt[]>([]);
  const [gesloten, setGesloten] = useState<{ ctx: string; id: string }[]>([]);
  const [bezigIn, setBezigIn] = useState<string | null>(null);

  const huidige = useRef(sleutel);
  useEffect(() => {
    huidige.current = sleutel;
  }, [sleutel]);

  const bezig = bezigIn !== null && bezigIn === sleutel;

  const stuur = useCallback(
    async (tekst: string) => {
      if (!ctx || bezigIn === sleutel) return;
      const eigen = sleutel;
      const beurtSleutel = `les-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setBezigIn(eigen);
      // Beurten van een vorige context ruimen we hier op; in beeld stonden
      // ze al niet meer (de lijst filtert op de huidige context).
      setLokaal((oud) => [
        ...oud.filter((b) => b.ctx === eigen),
        {
          sleutel: beurtSleutel,
          ctx: eigen,
          vraag: tekst,
          les: null,
          fout: null,
        },
      ]);
      const zet = (deel: Partial<LesBeurt>) =>
        setLokaal((oud) =>
          oud.map((b) => (b.sleutel === beurtSleutel ? { ...b, ...deel } : b)),
        );
      try {
        const les = await leerDitApi.maak(tekst, ctx);
        zet({ les });
      } catch (e) {
        zet({ fout: lesFout(e, "Er is geen les gemaakt") });
      } finally {
        setBezigIn((nu) => (nu === eigen ? null : nu));
      }
    },
    [ctx, sleutel, bezigIn],
  );

  const sluit = useCallback((beurt: LesBeurt) => {
    setLokaal((oud) => oud.filter((b) => b.sleutel !== beurt.sleutel));
    const les = beurt.les;
    if (les) {
      const ctxNu = huidige.current;
      setGesloten((oud) => [
        ...oud.filter((g) => g.ctx === ctxNu),
        { ctx: ctxNu, id: les.id },
      ]);
    }
  }, []);

  const beurten = useMemo<LesBeurt[]>(() => {
    const hier = lokaal.filter((b) => b.ctx === sleutel);
    const lokaleIds = new Set(hier.flatMap((b) => (b.les ? [b.les.id] : [])));
    const dicht = new Set(
      gesloten.filter((g) => g.ctx === sleutel).map((g) => g.id),
    );
    const vanServer: LesBeurt[] = (lijst.data ?? [])
      .filter((l) => !lokaleIds.has(l.id) && !dicht.has(l.id))
      .map((l) => ({
        sleutel: `server-${l.id}`,
        vraag: l.invoer,
        les: l,
        fout: null,
      }));
    return [
      ...vanServer,
      ...hier.map(({ sleutel: s, vraag, les, fout }) => ({
        sleutel: s,
        vraag,
        les,
        fout,
      })),
    ];
  }, [lokaal, gesloten, lijst.data, sleutel]);

  const laadFout = lijst.isError
    ? "Eerdere lessen voor deze pagina konden niet worden opgehaald. Nieuwe lessen werken wel."
    : null;

  return { beurten, bezig, stuur, sluit, laadFout };
}
